import React, { useEffect } from "react";
import {
  defineMessages,
  FormattedMessage as T,
  useIntl,
  IntlShape,
} from "react-intl";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import RestoreIcon from "@material-ui/icons/Restore";
import Warning from "@material-ui/icons/Warning";
import { createFilterOptions } from "@material-ui/lab/Autocomplete";
import clsx from "clsx";
import {
  useForm,
  OnSubmit,
  UseFormOptions,
  Controller,
  EventFunction,
} from "react-hook-form";
import * as yup from "yup";

import { CarModel, CarBrand, Country } from "@egis-smartcity/parking";
import { noop } from "../../../utils";
import CustomAutocomplete from "./CustomAutocomplete";
import Ocr, { OcrProps } from "../Ocr";
import useCityConfig from "../../../hooks/useCityConfig";
import useCommonConfig from "../../../hooks/useCommonConfig";

const useStyles = makeStyles((theme: Theme) => ({
  /* Styles applied to the root element. */
  root: {
    width: "100%",
    height: "100%",
  },
  /* Styles applied to the control header. */
  controlHeader: {
    height: 48,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 8,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.shape.borderRadius,
  },
  warningSiv: {
    marginLeft: 15,
    color: "yellow",
    display: "flex",
    alignItems: "center",
  },
  cancelButton: {
    width: 24,
    height: 24,
    border: "thin solid white",
    borderRadius: "50%",
  },
  /* Styles applied to the control form. */
  controlForm: {
    width: "100%",
    height: "calc(100% - 48px)",
    padding: 8,
    overflow: "auto",
  },
  resetButton: {
    backgroundColor: "white",
    color: theme.palette.primary.main,
    borderRadius: 4,
    height: 32,
    width: 32,
    "&:hover": {
      backgroundColor: theme.palette.grey[300],
      transition: "all 150ms ease-in-out",
    },
  },
}));

const msg = defineMessages({
  title: {
    id: "controlForm.title",
    description: "Title of the control form.",
    defaultMessage: "Ascertainment",
  },
  warningSiv: {
    id: "controlForm.warningSiv",
    description: "Warning SIV missing values",
    defaultMessage: "Missing SIV values",
  },
  restoreButtonLabel: {
    id: "controlForm.restoreButton.label",
    description: "Message displayed to labeled the restore button.",
    defaultMessage: "Reset",
  },
  licensePlateLabel: {
    id: "controlForm.licensePlate.label",
    description: "Message displayed to labeled the licensePlate field.",
    defaultMessage: "License plate",
  },
  licensePlateRequired: {
    id: "locationControl.licensePlate.required",
    description: "Message displayed when the licensePlate field is empty.",
    defaultMessage: "The license plate is required",
  },
  vehicleTypeLabel: {
    id: "controlForm.vehicleType.label",
    description: "Message displayed to labeled the vehicleType field.",
    defaultMessage: "Vehicle type",
  },
  vehicleTypeRequired: {
    id: "locationControl.vehicleType.required",
    description: "Message displayed when the vehicleType field is empty.",
    defaultMessage: "The vehicule type is required",
  },
  carBrandLabel: {
    id: "controlForm.carBrand.label",
    description: "Message displayed to labeled the carBrand field.",
    defaultMessage: "Car brand",
  },
  carBrandRequired: {
    id: "locationControl.carBrand.required",
    description: "Message displayed when the carBrand field is empty.",
    defaultMessage: "The car brand is required",
  },
  carModelLabel: {
    id: "controlForm.carModel.label",
    description: "Message displayed to labeled the carModel field.",
    defaultMessage: "Car model",
  },
  carModelRequired: {
    id: "locationControl.carModel.required",
    description: "Message displayed when the carModel field is empty.",
    defaultMessage: "The car model is required",
  },
  countryLabel: {
    id: "controlForm.country.label",
    description: "Message displayed to labeled the country field.",
    defaultMessage: "Country",
  },
  countryRequired: {
    id: "locationControl.country.required",
    description: "Message displayed when the country field is empty.",
    defaultMessage: "The country is required",
  },
  timeLabel: {
    id: "controlForm.time.label",
    description: "Message displayed to labeled the time field.",
    defaultMessage: "Time",
  },
  dateLabel: {
    id: "controlForm.date.label",
    description: "Message displayed to labeled the date field.",
    defaultMessage: "Date",
  },
});

export function getControlFormSchema(
  y: typeof yup,
  t: IntlShape["formatMessage"],
  isNfpsMode: boolean = false,
  nfpsOptionalFieldsEnabled: boolean = false
) {
  const shouldMakeOptional = isNfpsMode && nfpsOptionalFieldsEnabled;
  return y.object().shape({
    licensePlate: y.string().required(t(msg.licensePlateRequired)),
    vehicleType: y.object({
      label: y.string().required(t(msg.vehicleTypeRequired)),
      value: y.string().nullable(),
    }),
    carBrand: shouldMakeOptional
      ? y
          .object({
            id: y.string(),
            label: y.string(),
            value: y.string(),
          })
          .nullable()
      : y
          .object({
            id: y.string().required(t(msg.carBrandRequired)),
            label: y.string().required(t(msg.carBrandRequired)),
            value: y.string().required(t(msg.carBrandRequired)),
          })
          .required(t(msg.carBrandRequired)),
    carModel: shouldMakeOptional
      ? y
          .object({
            id: y.string(),
            label: y.string(),
            value: y.string(),
            carBrand: y
              .object({
                id: y.string(),
                label: y.string(),
                value: y.string(),
              })
              .nullable(),
          })
          .nullable()
      : y
          .object({
            id: y.string().required(t(msg.carModelRequired)),
            label: y.string().required(t(msg.carModelRequired)),
            value: y.string().required(t(msg.carModelRequired)),
            carBrand: y
              .object({
                id: y.string().required(t(msg.carModelRequired)),
                label: y.string().required(t(msg.carModelRequired)),
                value: y.string().required(t(msg.carModelRequired)),
              })
              .required(t(msg.carModelRequired)),
          })
          .nullable(),
    country: y
      .object({
        id: y.string().required(t(msg.countryRequired)),
        label: y.string().required(t(msg.countryRequired)),
        value: y.string().required(t(msg.countryRequired)),
      })
      .required(t(msg.countryRequired)),
    dateTime: y.string().required(),
  });
}

export type ControlFormValues = yup.InferType<
  ReturnType<typeof getControlFormSchema>
>;

export type VehicleType = ControlFormValues["vehicleType"];

export const emptyControlFormValues: ControlFormValues = {
  licensePlate: "",
  vehicleType: {
    label: "",
    value: "",
  },
  carBrand: {
    id: "",
    label: "",
    value: "",
  },
  carModel: null,
  country: {
    id: "",
    label: "",
    value: "",
  },
  dateTime: "",
};

export type ControlFormProps = Omit<
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
  "onSubmit"
> & {
  values?: UseFormOptions<ControlFormValues>["defaultValues"];
  initialValues?: UseFormOptions<ControlFormValues>["defaultValues"];
  triggerControlFormValidation?: boolean;
  showValidationErrors?: boolean;
  onLicensePlateBlur?(licensePlate: string): void;
  onSubmit?: OnSubmit<ControlFormValues>;
  onValuesChange?(values: ControlFormValues): void;
  OcrProps?: OcrProps;
  isSivDataAvailable?: boolean;
};

function ControlForm(props: ControlFormProps) {
  ///////////////// PROPS, HOOKS AND STATE SETUP////////////////////////////////

  const {
    className,
    values = emptyControlFormValues,
    initialValues = emptyControlFormValues,
    triggerControlFormValidation = false,
    showValidationErrors = false,
    onLicensePlateBlur = noop,
    onSubmit = noop,
    onValuesChange = noop,
    OcrProps,
    isSivDataAvailable,
    ...other
  } = props;
  const { formatMessage: t } = useIntl();
  const { cityConfig } = useCityConfig();
  const {
    control,
    errors,
    handleSubmit,
    register,
    reset,
    setValue,
    triggerValidation,
    unregister,
    watch,
  } = useForm<ControlFormValues>({
    defaultValues: values,
    mode: "onChange", // https://github.com/react-hook-form/react-hook-form/issues/611
    validationSchema: getControlFormSchema(
      yup,
      t,
      false,
      cityConfig?.review?.nfpsOptionalFieldsEnabled === true
    ),
  });
  const { carBrands, carModels, countries } = useCommonConfig();

  ///////////////// CSS ////////////////////////////////////////////////////////

  const classes = useStyles();

  const rootClasses = React.useMemo(() => {
    return clsx(classes.root, className);
  }, [className, classes.root]);

  ///////////////// INIT AND SIDE EFFECT(S) ////////////////////////////////////

  // Manually register the dateTime field as we just want to display it.
  React.useEffect(() => {
    register({ name: "dateTime" });

    return () => {
      unregister("dateTime");
    };
  }, [register, unregister]);

  const carBrand = watch("carBrand"); // Watch the `carBrand` field to constraint the options of the `carModel` field.
  const licensePlate = watch("licensePlate");

  const watchedValues = watch();

  const lastSentRef = React.useRef<string>("");

  React.useEffect(() => {
    const serialized = JSON.stringify(watchedValues);

    if (serialized === lastSentRef.current) return;
    lastSentRef.current = serialized;

    onValuesChange(watchedValues as ControlFormValues);
  }, [watchedValues, onValuesChange]);

  // The defaultValues are cached into useForm, so we have to reset them
  // if the values property change.
  React.useEffect(() => {
    reset(values);
    // On synchronise le "dernier envoyé" avec les nouvelles values
    lastSentRef.current = JSON.stringify(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.dateTime, reset]);

  // If triggerControlFormValidation is true,
  // unlock the form and run the validation.
  React.useEffect(() => {
    if (!triggerControlFormValidation) return;

    triggerValidation();
  }, [triggerControlFormValidation, triggerValidation]);

  ///////////////// WORKFLOW ///////////////////////////////////////////////////

  const handleBlur: EventFunction = React.useCallback(
    ([_event, data]) => {
      handleSubmit(onSubmit)();

      return data; // Needed by the Controller component to work properly in `onBlur` mode.
    },
    [handleSubmit, onSubmit]
  );

  const handleLicensePlateBlur: EventFunction = React.useCallback(
    ([_event, data]) => {
      handleSubmit(onSubmit)();

      onLicensePlateBlur(licensePlate);

      return data; // Needed by the Controller component to work properly in `onBlur` mode.
    },
    [handleSubmit, licensePlate, onLicensePlateBlur, onSubmit]
  );

  //reinitialize the whole form
  const handleCancelButtonClick = () => {
    reset(initialValues);
  };

  // Add the brand and the model in the list if they are defined in the control but not defined in the DB storing all the brands and models
  useEffect(() => {
    // Brand
    const researched_brand = carBrands?.find((current_brand) => {
      return (
        current_brand.value.toLocaleLowerCase() ===
        values?.carBrand?.value?.toLocaleLowerCase()
      );
    });

    if (!researched_brand && values?.carBrand?.value) {
      carBrands?.unshift({
        id: values.carBrand.id!,
        label: values?.carBrand?.label!,
        value: values?.carBrand?.value!,
        position: 0,
      });
    }

    // Model
    const researched_model = carModels?.find((current_model) => {
      return (
        current_model.value.toLocaleLowerCase() ===
        values?.carModel?.value?.toLocaleLowerCase()
      );
    });

    if (!researched_model && values?.carModel?.value) {
      carModels?.unshift({
        id: values?.carModel?.id!,
        label: values?.carModel?.label!,
        value: values?.carModel?.value!,
        carBrand: {
          id: values?.carBrand?.id!,
          label: values?.carBrand?.label!,
          value: values?.carBrand?.value!,
        },
      });
    }
  }, [carBrands, carModels, values]);

  // Helper pour extraire le message d'erreur de manière sûre
  function getErrorMessage(error: any): string | undefined {
    if (!error) return undefined;
    if (typeof error === "string") return error;
    if (error.message) return error.message;
    if (error.value?.message) return error.value.message;
    if (error.label?.message) return error.label.message;
    if (error.id?.message) return error.id.message;
    return undefined;
  }

  // Helper pour vérifier si un champ est requis
  function isFieldRequired(error: any): boolean {
    if (!error) return false;
    if (error.type === "required") return true;
    if (error.value?.type === "required") return true;
    if (error.label?.type === "required") return true;
    if (error.id?.type === "required") return true;
    return false;
  }

  // Vérifier si les champs optionnels NFPS sont activés
  const nfpsOptionalFieldsEnabled =
    cityConfig?.review?.nfpsOptionalFieldsEnabled === true;

  // Fonction pour déterminer si un champ doit afficher une étoile
  const shouldShowRequired = (fieldName: "carBrand" | "carModel"): boolean => {
    if (!nfpsOptionalFieldsEnabled) return true;
    if (!showValidationErrors) return false;
    return true;
  };

  // Fonction pour déterminer si un champ doit afficher une erreur
  const shouldShowError = (fieldName: "carBrand" | "carModel"): boolean => {
    if (!nfpsOptionalFieldsEnabled) return true;
    if (!showValidationErrors) return false;
    return true;
  };

  return (
    <div className={rootClasses} {...other}>
      <div className={classes.controlHeader}>
        <Typography
          variant="h6"
          noWrap
          style={{ display: "flex", alignItems: "center" }}
        >
          <T {...msg.title} />
          {isSivDataAvailable === false && cityConfig?.review.siv && (
            <div className={classes.warningSiv}>
              <Warning fontSize="large"></Warning>
              <Typography variant="h6">{t(msg.warningSiv)}</Typography>
            </div>
          )}
        </Typography>
        <Tooltip title={t(msg.restoreButtonLabel)}>
          <span>
            <IconButton
              className={classes.resetButton}
              aria-label={t(msg.restoreButtonLabel)}
              color="inherit"
              disableRipple
              onClick={handleCancelButtonClick}
            >
              <RestoreIcon />
            </IconButton>
          </span>
        </Tooltip>
      </div>
      <form className={classes.controlForm}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Ocr {...OcrProps} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Controller
              name="licensePlate"
              as={
                <TextField
                  label={t(msg.licensePlateLabel)}
                  required={errors.licensePlate?.type === "required"}
                  error={!!errors.licensePlate}
                  helperText={
                    !!errors.licensePlate && errors.licensePlate.message
                  }
                  fullWidth
                />
              }
              control={control}
              onBlur={handleLicensePlateBlur}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Controller
              name="country"
              as={
                <CustomAutocomplete<Country>
                  TextFieldProps={{
                    label: t(msg.countryLabel),
                    required: errors.country?.value?.type === "required",
                    error: !!errors.country,
                    helperText:
                      !!errors.country && errors.country?.value?.message,
                    fullWidth: true,
                  }}
                  options={countries}
                  getOptionLabel={(option: Country) => option.value}
                  getOptionSelected={(option: Country, value: Country) =>
                    option.value === value.value
                  }
                  filterOptions={createFilterOptions({
                    matchFrom: "start",
                    stringify: (option: Country) => option.value,
                  })}
                />
              }
              control={control}
              onChange={([_event, country]) => {
                if (country === null) return emptyControlFormValues.country;
                return country;
              }}
              onBlur={handleBlur}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="vehicleType"
              as={
                <CustomAutocomplete<VehicleType>
                  TextFieldProps={{
                    label: t(msg.vehicleTypeLabel),
                    required: errors.vehicleType?.label?.type === "required",
                    error: !!errors.vehicleType,
                    helperText:
                      !!errors.vehicleType &&
                      errors.vehicleType?.label?.message,
                    fullWidth: true,
                  }}
                  options={
                    (cityConfig?.review.fps.vehicleType as {
                      label: string;
                      value: string;
                    }[]) || []
                  }
                  getOptionLabel={(option: VehicleType) => option.label}
                  getOptionSelected={(
                    option: VehicleType,
                    value: VehicleType
                  ) => option.value === value.value}
                  filterOptions={createFilterOptions({
                    matchFrom: "start",
                    stringify: (option: VehicleType) => option.label,
                  })}
                />
              }
              control={control}
              onChange={([_event, vehicleType]) => {
                if (vehicleType === null) {
                  return emptyControlFormValues.vehicleType;
                }
                return vehicleType;
              }}
              onBlur={handleBlur}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="carBrand"
              as={
                <CustomAutocomplete<CarBrand | null>
                  TextFieldProps={{
                    label: t(msg.carBrandLabel),
                    required:
                      shouldShowRequired("carBrand") &&
                      isFieldRequired(errors.carBrand),
                    error: shouldShowError("carBrand") && !!errors.carBrand,
                    helperText: shouldShowError("carBrand")
                      ? getErrorMessage(errors.carBrand)
                      : undefined,
                    fullWidth: true,
                  }}
                  options={carBrands}
                  getOptionLabel={(option: CarBrand | null) =>
                    option !== null ? option.label : ""
                  }
                  getOptionSelected={(
                    option: CarBrand | null,
                    value: CarBrand | null
                  ) =>
                    option !== null &&
                    value !== null &&
                    option.value === value.value
                  }
                  filterOptions={createFilterOptions({
                    matchFrom: "start",
                    stringify: (option: CarBrand | null) =>
                      option !== null ? option.label : "",
                  })}
                />
              }
              control={control}
              onChange={([_event, carBrand]) => {
                setValue("carModel", emptyControlFormValues.carModel);
                if (carBrand === null) return emptyControlFormValues.carBrand;
                return carBrand;
              }}
              onBlur={handleBlur}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="carModel"
              as={
                <CustomAutocomplete<CarModel | null>
                  TextFieldProps={{
                    label: t(msg.carModelLabel),
                    required:
                      shouldShowRequired("carModel") &&
                      isFieldRequired(errors.carModel),
                    error: shouldShowError("carModel") && !!errors.carModel,
                    helperText: shouldShowError("carModel")
                      ? getErrorMessage(errors.carModel)
                      : undefined,
                    fullWidth: true,
                  }}
                  options={
                    carBrand === null || carBrand.id === ""
                      ? carModels
                      : carModels?.filter(
                          (cm: CarModel) => cm.carBrand.id === carBrand?.id
                        )
                  }
                  disabled={
                    carBrand !== null &&
                    carBrand.id !== "" &&
                    carModels?.filter(
                      (cm: CarModel) => cm.carBrand.id === carBrand?.id
                    ).length === 0
                  }
                  getOptionLabel={(option: CarModel | null) =>
                    option !== null
                      ? option.carBrand.label === ""
                        ? option.label
                        : `${option.label} (${option.carBrand.label})`
                      : ""
                  }
                  getOptionSelected={(
                    option: CarModel | null,
                    value: CarModel | null
                  ) =>
                    option !== null &&
                    value !== null &&
                    option.value === value.value
                  }
                  filterOptions={createFilterOptions({
                    matchFrom: "start",
                    stringify: (option: CarModel | null) =>
                      option !== null
                        ? `${option.label} (${option.carBrand.label})`
                        : "",
                  })}
                />
              }
              control={control}
              onChange={([_event, carModel]) => {
                if (carModel === null) return emptyControlFormValues.carModel;
                setValue("carBrand", carModel.carBrand);
                triggerValidation("carBrand");
                return carModel;
              }}
              onBlur={handleBlur}
            />
          </Grid>
        </Grid>
      </form>
    </div>
  );
}

export default ControlForm;
